import { HttpClient } from "../http";
import type { Automation, Trigger, Listener, Keyword } from "../types";

export class AutomationService extends HttpClient {
  constructor() {
    super(process.env.NEXT_PUBLIC_API_URL!);
  }

  async getAll() {
    return this.get<Automation[]>("/api/automations");
  }

  async getById(id: string) {
    return this.get<Automation>(`/api/automations/${id}`);
  }

  async create(payload: Partial<Automation>) {
    return this.post<Automation>("/api/automations", payload);
  }

  async update(id: string, payload: Partial<Automation>) {
    return this.patch<Automation>(`/api/automations/${id}`, payload);
  }

  async remove(id: string) {
    return this.delete<{ success: boolean }>(`/api/automations/${id}`);
  }

  async toggle(id: string, isActive: boolean) {
    return this.patch<Automation>(`/api/automations/${id}/toggle`, { isActive });
  }

  async getTriggers(automationId: string) {
    return this.get<Trigger[]>(`/api/automations/${automationId}/triggers`);
  }

  async createTrigger(automationId: string, type: "COMMENT" | "DM") {
    return this.post<Trigger>(`/api/automations/${automationId}/triggers`, { type });
  }

  async deleteTrigger(triggerId: string) {
    return this.delete<{ success: boolean }>(`/api/triggers/${triggerId}`);
  }

  async getListener(automationId: string) {
    return this.get<Listener | null>(`/api/automations/${automationId}/listener`);
  }

  async createListener(automationId: string, data: Partial<Listener>) {
    return this.post<Listener>(`/api/automations/${automationId}/listener`, data);
  }

  async updateListener(automationId: string, data: Partial<Listener>) {
    return this.patch<Listener>(`/api/automations/${automationId}/listener`, data);
  }

  async deleteListener(automationId: string) {
    return this.delete<{ success: boolean }>(`/api/automations/${automationId}/listener`);
  }

  async getKeywords(automationId: string) {
    return this.get<Keyword[]>(`/api/automations/${automationId}/keywords`);
  }

  async createKeyword(automationId: string, word: string) {
    return this.post<Keyword>(`/api/automations/${automationId}/keywords`, { word });
  }

  async createKeywordsBatch(automationId: string, words: string[]) {
    return this.post<Keyword[]>(`/api/automations/${automationId}/keywords/batch`, { words });
  }

  async deleteKeyword(keywordId: string) {
    return this.delete<{ success: boolean }>(`/api/keywords/${keywordId}`);
  }

  async getInstagramPosts(accountId: string) {
    return this.get<any[]>(`/api/instagram/accounts/${accountId}/posts`);
  }

  async savePosts(automationId: string, posts: any[]) {
    return this.post<any>(`/api/automations/${automationId}/posts/batch`, { posts });
  }

  async getFlow(automationId: string) {
    return this.get<{ nodes: FlowNode[]; edges: FlowEdge[] }>(`/api/automations/${automationId}/flow`);
  }

  async replaceFlow(automationId: string, nodes: FlowNode[], edges: FlowEdge[]) {
    return this.put<{ nodes: FlowNode[]; edges: FlowEdge[] }>(`/api/automations/${automationId}/flow`, { nodes, edges });
  }
}

export type FlowNodeType =
  | "TRIGGER_KEYWORD"
  | "TRIGGER_COMMENT"
  | "TRIGGER_DM"
  | "CONDITION_KEYWORD_MATCH"
  | "CONDITION_POST_SCOPE"
  | "ACTION_SEND_DM"
  | "ACTION_REPLY_COMMENT"
  | "ACTION_LIKE_COMMENT";

export interface FlowNode {
  id: string;
  automationId?: string;
  type: FlowNodeType;
  position: { x: number; y: number };
  config: Record<string, any>;
  sequence?: number;
}

export interface FlowEdge {
  id?: string;
  automationId?: string;
  sourceNodeId: string;
  targetNodeId: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
}
